import Immutable from "seamless-immutable";
import { camelCase, isEmpty, isEqual, isFunction } from "lodash";
import { sanitize } from "dompurify";
import {
  PRODUCT_KEY,
  RECOMMENDED_PRODUCT_IMAGE_KEY,
  IMAGE_ALT_TEXT_KEY,
  GALLERY_THUMBNAIL_IMAGE_KEY,
  GALLERY_PRODUCT_IMAGE_KEY,
  SWATCH_IMAGE_KEY,
  COLOR_SPECIFIC_GALLERY_IMAGES_KEY,
  COLOR_SPECIFIC_GALLERY_IMAGES_ALT_TEXT_KEY,
  CART_DESCRIPTION_KEY,
  IS_NEW_KEY,
  SHOPIFY_QUERY_LIMIT,
  SHOPIFY_PAGE_SIZE_QUERY_LIMIT,
  PRODUCT_IS_VARIANT_KEY,
  RECOMMENDED_PRODUCTS_KEY,
  EDITORIAL_BLURB_COPY_KEY,
  METAFIELD_LIST_DELIMITER,
  CUSTOM_PRODUCT_PAGE_HANDLES,
  SEO_PRODUCT_DESCRIPTION,
  VARIANT_SLUG,
  SHOPIFY_PRODUCT_FULFILLMENT_MAPPING,
  FULFILLMENT_SERVICE_KEY,
} from "./productConstants";
import ApiClient from "../api";

const SLEEP_GOAL_HANDLE_PREFIX = "sleep-goal-";
export const SHOPIFY_SWATCH_OPTION_NAME = "Color";

/* Shop Utilities */
export const getProductKeyFromHandle = (handle) => camelCase(handle);

export const getSleepGoalCollectionKeyFromHandle = (handle) =>
  getProductKeyFromHandle(handle.replace(SLEEP_GOAL_HANDLE_PREFIX, ""));

export const getSleepGoalCollection = (collections) =>
  collections.filter((collection) =>
    collection.handle.startsWith(SLEEP_GOAL_HANDLE_PREFIX)
  );

export const getProductSleepGoalCollectionKey = (collections) => {
  const [sleepGoalCollection] = getSleepGoalCollection(collections);

  return getSleepGoalCollectionKeyFromHandle(sleepGoalCollection.handle);
};

export const getMatchingSleepGoalCollection = (url, collectionData) => {
  return Object.values(collectionData).find(
    (collection) => collection.url === url
  );
};

export const getCollectionLandingPagePath = (handle) =>
  `/dream-shop/collections/${handle}`;

export const getProductDetailPagePath = (handle) => {
  // Default sleep product use case - dream shop product page
  let path = `/dream-shop/${handle}`;

  // Use a custom Hatch product page (used for Rest, Rest+, and Restore)
  if (CUSTOM_PRODUCT_PAGE_HANDLES.includes(handle)) {
    path = `/${handle}`;
  }

  return path;
};

export const getTotalCartQuantity = (lineItems) => {
  return lineItems.reduce((total, currentItem) => {
    return total + currentItem.quantity;
  }, 0);
};

export const getProductName = (name, variant) => {
  return variant ? `${name} - ${variant}` : name;
};

export const constructImageData = ({ src, altText }, defaultAltText = "") => ({
  src,
  alt: altText || defaultAltText,
});
/**
 * This is a little more specific to image gallery image
 * data creation and is not tied to needing specific
 * variants objects passed in.
 * @param galleryItems: [{
    thumbnail: { src, altText },
    product: { src, altText },
  }]
 */
export const constructImageGalleryImages = (galleryItems) => {
  return galleryItems.reduce(
    (images, item) => {
      images.thumbnailImages.push({
        src: item.thumbnail.src,
        alt: item.thumbnail.altText,
      });

      images.featuredImages.push({
        src: item.product.src,
        alt: item.product.altText,
      });

      return images;
    },
    { thumbnailImages: [], featuredImages: [] }
  );
};

/**
 * This is specific to color picker image galleries.
 * @param galleryItems: [{
    thumbnail: { src, altText },
  }]
 */
export const constructSwatchPickerImages = (galleryItems) => {
  return galleryItems.reduce(
    (images, item) => {
      images.thumbnailImages.push({
        src: item.thumbnail.src,
        alt: item.thumbnail.altText,
      });

      return images;
    },
    { thumbnailImages: [] }
  );
};

export const isInCart = (variantId, orderedLineItems) =>
  orderedLineItems.includes(variantId);

/**
 * For use in ShopReducer.js
 */

// 'orderedLineItems' can just be an array of the variant IDs
// Once item has been successfully added to Shopify cart,
// add to beginning of 'orderedLineItems' array to preserve ordering
export const addLineItemToOrderedLineItems = (orderedLineItems, variantId) => {
  const mutableOrderedLineItems = Immutable.isImmutable(orderedLineItems)
    ? orderedLineItems.asMutable()
    : orderedLineItems;
  // If variant ID doesn't exist in list of line items
  // add to beginning of the list as it's a new cart item.
  // Otherwise just return original 'orderedLineItems'
  if (!mutableOrderedLineItems.includes(variantId)) {
    mutableOrderedLineItems.unshift(variantId);
  }

  return mutableOrderedLineItems;
};

// Once removal of line item is successful,
// filter out that variant ID from the 'orderedLineItems' array
export const removeLineItemFromOrderedLineItems = (
  orderedLineItems,
  variantId
) => {
  const mutableOrderedLineItems = Immutable.isImmutable(orderedLineItems)
    ? orderedLineItems.asMutable()
    : orderedLineItems;

  return mutableOrderedLineItems.filter((id) => id !== variantId);
};

// This is for the initial release of this feature. For users with items
// already in their cart, we need to set the initial value for 'orderedLineItems'.
export const setInitialOrderedLineItems = (lineItems, orderedLineItems) => {
  // If `orderedLineItems` already has items in it,
  // we can assume that we have already set orderedLineItems
  // so just return the persisted 'orderedLineItems'
  if (orderedLineItems.length > 0) {
    return orderedLineItems;
  }

  // Create new 'orderedLineItems' array using the
  // checkout.lineItems variant ID and using that line item
  // order as the base order.
  if (lineItems.length > 0) {
    return lineItems.map((lineItem) => lineItem.variant.id);
  }

  return [];
};

export const getProductData = (collection, handle) => {
  if (isEmpty(collection)) {
    return {};
  }

  const productKey = getProductKeyFromHandle(handle);

  return collection[productKey] || {};
};

/**
 * Get variant data from Shopify's query response
 *
 * Key is a parameter here since we use a specific variant
 * for some products (Ex. For Rest+, we use the second variant).
 */
export const getVariantData = (productShopifyData, key = 0) => {
  if (isEmpty(productShopifyData)) {
    return {};
  }

  return productShopifyData.variants
    ? productShopifyData.variants[key]
    : productShopifyData;
};

export const getVariantDataByMetafield = ({
  variants,
  metafieldKey,
  metafieldValue,
}) => {
  return variants.find(
    (variant) => getMetafield(variant, metafieldKey) === metafieldValue
  );
};

export const constructMetafieldsObject = (metafields) => {
  return metafields.reduce((obj, item) => {
    obj[item.key] = item.value;

    return obj;
  }, {});
};

export const constructVariantOptionsObject = (selectedOptions) => {
  return selectedOptions.reduce((obj, { name, value }) => {
    obj[name] = value;

    return obj;
  }, {});
};

export const getMetafield = (productData, key) => {
  if (isEmpty(productData) || !key) {
    return null;
  }

  const metafield = productData.metafields.find((field) => field.key === key);
  return metafield ? metafield.value : null;
};

export const getShopifyVariantData = (productShopifyData = {}, variantId) => {
  if (isEmpty(productShopifyData) || !variantId) {
    return {};
  }

  return productShopifyData.variants.find(
    (variant) => variant.id === variantId
  );
};

export const getLineItemDataByVariantId = (
  lineItems = null,
  variantId = null
) => {
  if (!lineItems || !variantId) {
    console.warn('"lineItems" and "variantId" are required params');
    return {};
  }

  return lineItems.find((item) => variantId === item.variant.id);
};

export const getLineItemCustomAttribute = (lineItem, attributeKey) => {
  if (!lineItem || !attributeKey) {
    console.warn('"lineItem" and "attributeKey" are required params');

    return {};
  }

  const attribute = lineItem.customAttributes.find(
    (attr) => attr.key === attributeKey
  );

  if (attribute) {
    return attribute.value;
  }

  return null;
};

export const getLineItemDiscountsApplied = (lineItem) => lineItem.discounts;

/**
 * Every Shopify product has at least 1 variant which is a dupe of the main product.
 * A way to check to see if a product has other variants is to just check the # of
 * variants in the Shopify product data.
 *
 * Note: Need to remove other price variants for Rest+ so that this will work
 */
export const productHasOtherVariants = (productShopifyData = {}) => {
  if (isEmpty(productShopifyData)) {
    return false;
  }

  return productShopifyData.variants && productShopifyData.variants.length > 1;
};

export const productHasColorOptions = (productShopifyData = {}) => {
  if (isEmpty(productShopifyData)) {
    return false;
  }

  return productShopifyData.options.some(
    (option) => option.name === SHOPIFY_SWATCH_OPTION_NAME
  );
};

export const createCustomAttributesFromMetafields = (metafields = []) => {
  if (isEmpty(metafields)) {
    return [];
  }

  // We only need the key and value attributes from metafields
  return [...metafields].map(({ key, value }) => ({ key, value }));
};

export const constructCtaProductData = (
  productShopifyData = {},
  variantId = null
) => {
  const hasVariants = productHasOtherVariants(productShopifyData);
  const data = { productName: productShopifyData.title || null };
  const productKeyFromHandle = {
    value: getProductKeyFromHandle(productShopifyData.handle),
    key: PRODUCT_KEY,
  };

  // If this product has other options/variants AND a variantId is specified,
  // locate that specific variant's product data in the Shopify object
  if (hasVariants && variantId) {
    const variantData = getShopifyVariantData(productShopifyData, variantId);
    const variantCustomAttributes = createCustomAttributesFromMetafields(
      variantData.metafields
    );

    return {
      ...data,
      // This represents the overarching product name (eg. Coverlets)
      variantId: variantData.id || null,
      // This is the product variant name
      variantName: variantData.title || null,
      variantData,
      customAttributes: [
        ...variantCustomAttributes,
        productKeyFromHandle,
        // Note: customAttributes requires the key and value to be strings
        { value: "true", key: PRODUCT_IS_VARIANT_KEY },
      ],
    };
  }

  const productCustomAttributes = createCustomAttributesFromMetafields(
    productShopifyData.metafields
  );
  const variantData = getVariantData(productShopifyData);

  // If no other options/variants exist, just use the main product
  return {
    ...data,
    variantId: variantData ? variantData.id : null,
    variantData,
    customAttributes: [productKeyFromHandle, ...productCustomAttributes],
  };
};

// Scan checkout > line items
export function getLineItemsWithFulfillmentAttribute(lineItems) {
  return lineItems.filter(
    ({ variant: { sku } }) => !!SHOPIFY_PRODUCT_FULFILLMENT_MAPPING[sku]
  );
}

export function getProductsWithoutFulfillmentAttribute(lineItems) {
  return lineItems.filter(
    (lineItem) => !getLineItemCustomAttribute(lineItem, FULFILLMENT_SERVICE_KEY)
  );
}

export function updateLineItemsWithFulfillmentServiceAttribute(lineItems) {
  return lineItems.map(
    ({ customAttributes, variant: { sku, id: variantId }, id, quantity }) => {
      // Can only pass in 'key' and 'value' properties in update call
      const currentCustomAttributes = customAttributes.map(
        ({ key, value }) => ({ key, value })
      );

      return {
        variantId,
        id,
        quantity,
        customAttributes: [
          ...currentCustomAttributes,
          {
            key: FULFILLMENT_SERVICE_KEY,
            value: SHOPIFY_PRODUCT_FULFILLMENT_MAPPING[sku],
          },
        ],
      };
    }
  );
}

export const sanitizeMarkup = (markup, forbiddenAttr = []) =>
  sanitize(markup, { FORBID_ATTR: forbiddenAttr });

export const getShopifyProductCartImage = (images) => {
  return images[0];
};

export const getShopifyProductSliderImage = (images) => {
  return images[1];
};

export const getShopifyProductFeaturedImage = (images) => {
  return images[2];
};

export const getProductSeoImage = (images) => {
  const image = getShopifyProductSliderImage(images);
  if (image) {
    return image.src;
  }

  return null;
};

export const getShopifyProductUpsellImage = (productShopifyData) =>
  getMetafield(productShopifyData, RECOMMENDED_PRODUCT_IMAGE_KEY);

export const getShopifyProductImageAltText = (productShopifyData) =>
  getMetafield(productShopifyData, IMAGE_ALT_TEXT_KEY);

export const getShopifyProductCartDescription = (productShopifyData) =>
  getMetafield(productShopifyData, CART_DESCRIPTION_KEY);

export const getShopifyProductSeoDescription = (productShopifyData) =>
  getMetafield(productShopifyData, SEO_PRODUCT_DESCRIPTION);

export const getShopifyGalleryImages = (images, defaultAltText) => {
  return (
    // All images except the first and second ones (cart & silo image)
    images
      .filter((image, idx) => idx > 1)
      .map((image) => {
        const { src, altText } = image;

        return {
          src,
          alt: altText || defaultAltText,
        };
      })
  );
};

const collectWithProductsQuery = (collection) => {
  collection.add("title");
  collection.add("description");
  collection.add("handle");
  collection.add("image", (image) => {
    image.add("src");
    image.add("altText");
  });
  collection.addConnection(
    "products",
    { args: { first: SHOPIFY_QUERY_LIMIT } },
    (product) => {
      product.add("title");
      product.add("description");
      product.add("descriptionHtml");
      product.add("tags");
      product.add("handle");
      product.add("options", (options) => {
        options.add("name");
        options.add("values");
      });
      product.addConnection(
        "variants",
        { args: { first: SHOPIFY_QUERY_LIMIT } },
        (variants) => {
          variants.add("id");
          variants.add("product");
          variants.add("title");
          variants.add("price");
          variants.add("sku");
          variants.add("availableForSale");
          variants.add("image", (image) => {
            image.add("src");
            image.add("altText");
          });
          variants.add("selectedOptions", (option) => {
            option.add("name");
            option.add("value");
          });
          variants.addConnection(
            "metafields",
            { args: { first: SHOPIFY_QUERY_LIMIT } },
            (metafield) => {
              metafield.add("key");
              metafield.add("value");
            }
          );
        }
      );
      product.addConnection(
        "images",
        { args: { first: SHOPIFY_QUERY_LIMIT } },
        (image) => {
          image.add("src");
          image.add("altText");
        }
      );
      product.addConnection(
        "metafields",
        { args: { first: SHOPIFY_QUERY_LIMIT } },
        (metafield) => {
          metafield.add("key");
          metafield.add("value");
        }
      );
      product.addConnection(
        "collections",
        { args: { first: SHOPIFY_QUERY_LIMIT } },
        (productCollection) => {
          productCollection.add("title");
          productCollection.add("description");
          productCollection.add("handle");
        }
      );
    }
  );
};

/**
 *
 * @param {*string} searchTerm - eg. 'handle:sleep goal' or just 'sleep goal' to search across all fields
 *
 * https://shopify.dev/docs/storefront-api/reference/queryroot
 * https://shopify.dev/concepts/about-apis/search-syntax
 * https://help.shopify.com/en/manual/sell-online/online-store/storefront-search#prefix-search
 * https://shopify.dev/docs/storefront-api/reference/object/collection
 */
export const queryMultipleCollections = (searchTerm) => {
  const query = ApiClient.getShopify().graphQLClient.query((root) => {
    root.add("shop", (shop) => {
      shop.addConnection(
        "collections",
        { args: { first: 20, query: searchTerm } },
        collectWithProductsQuery
      );
    });
  });

  return ApiClient.getShopify()
    .graphQLClient.send(query)
    .then(({ model }) => model.shop.collections);
};

export const queryCollectionByHandle = (handle) => {
  const query = ApiClient.getShopify().graphQLClient.query((root) => {
    root.add("shop", (shop) => {
      shop.add(
        "collectionByHandle",
        { args: { handle } },
        collectWithProductsQuery
      );
    });
  });
  return ApiClient.getShopify()
    .graphQLClient.send(query)
    .then(({ model }) => {
      return ApiClient.getShopify().graphQLClient.fetchAllPages(
        model.shop.collectionByHandle,
        { pageSize: SHOPIFY_PAGE_SIZE_QUERY_LIMIT }
      );
    });
};

export const keyGraphCollectionDataList = (
  collectionList,
  handleFormatter = null
) => {
  return collectionList.reduce(
    (res, { products, title, image, description, handle }) => {
      const handleForKey = isFunction(handleFormatter)
        ? handleFormatter(handle)
        : handle;
      const key = getProductKeyFromHandle(handleForKey);

      // Key data by product_key
      res[key] = {
        products,
        metadata: { title, image, description, handle },
      };

      return res;
    },
    {}
  );
};

// This is used in ProductionOptionsSelector to store all available option
// combinations so we know which options we need to disable
// Setting the default "available" value to "false" which will be updated
// in `constructAvailableOptionCombinationsMap` below based on the
// matching variant's `availableForSale` value. It's set to `false` by default
// in case a variant is not found and if it's not, it should remain `false`.
export const constructAvailableOptionsMap = (values) =>
  values.reduce(
    (valuesMap, value) => ({
      ...valuesMap,
      [value]: false,
    }),
    {}
  );

// Create the base combo map
// This will be keyed by the option name whose values will be
// an object container all other option types and a boolean on whether
// it is available with this particular option.
export const constructAvailableOptionsComboBaseMap = (options) => {
  // This will create the base map with each option name as
  // a key and an empty object for its value
  // eg.
  //  {
  //    'Violet': { name: 'Color' },
  //    'Navy': { name: 'Color' },
  //    'White': { name: 'Color' },
  //    'Cotton': { name: 'Material' },
  //    'Silk': { name: 'Material' },
  //  }
  //
  const comboBase = options.reduce(
    (optionMap, { name, values }) => ({
      ...optionMap,
      ...values.reduce(
        (valueMap, value) => ({
          ...valueMap,
          [value]: { name },
        }),
        {}
      ),
    }),
    {}
  );
  // This takes the above and attaches the all option values outside of
  // the current option's option type. By default we set everything to `true`
  // and then in constructAvailableOptionCombinationsMap, we will set the correct value
  // based on the variant's `availableForSale` field value.
  // ie.
  //  {
  //    'White': {
  //      'Material': { Silk: true, Cotton: true },
  //    },
  //    'Violet': {
  //      'Material': { Silk: true, Cotton: true },
  //    },
  //    'Navy': {
  //      'Material': { Silk: true, Cotton: true },
  //    },
  //    'Silk': {
  //      'Color': { White: true, Violet: true, Navy: true },
  //    },
  //    'Cotton': {
  //      'Color': { White: true, Violet: true, Navy: true },
  //    },
  //  }
  //
  return options.reduce((finalComboBase, { name, values }) => {
    let copy = finalComboBase;

    for (const optionName in copy) {
      const { name: optionType } = copy[optionName];

      // ie. If this is Color > Violet, we don't need to add a map
      //    to all of the Color values and see if they're available. Choosing a value
      //    within an particular option group should not affect the availability of the other values
      //    in the same group.
      if (optionType && optionType !== name) {
        copy = Immutable.setIn(copy, [optionName], {
          [name]: constructAvailableOptionsMap(values),
        });
      }
    }

    return copy;
  }, comboBase);
};

export const flattenVariantSelectedOptions = (variants) =>
  variants.map(({ selectedOptions, availableForSale }) => [
    ...selectedOptions.map((option) => ({ ...option, availableForSale })),
  ]);

// @tedlin182 - See if can be optimized. Options lists aren't too long fortunately, so this runs relatively
//            quickly.
export const constructAvailableOptionCombinationsMap = (options, variants) => {
  const comboBase = constructAvailableOptionsComboBaseMap(options);

  return flattenVariantSelectedOptions(variants).reduce(
    (
      finalComboMap,
      [{ name, value, availableForSale }, ...rest] // Current set of selected options for a single variant
    ) => {
      let copy = finalComboMap;

      rest.forEach(({ name: restName, value: restValue }) => {
        copy = Immutable.setIn(
          copy,
          [value, restName, restValue],
          availableForSale
        );
        copy = Immutable.setIn(
          copy,
          [restValue, name, value],
          availableForSale
        );
      });

      return copy;
    },
    comboBase
  );
};

export const constructAvailableOptionsListFromVariants = (options, variants) =>
  options.map(({ name, values, ...rest }) => ({
    name,
    values: values.map((value) => {
      const { availableForSale } = variants.find(({ selectedOptions }) =>
        selectedOptions.find(({ value: optionValue }) => optionValue === value)
      );

      return {
        value,
        availableForSale,
      };
    }),
    ...rest,
  }));

export const getAllAvailableOptions = ({
  selectedOptions,
  options,
  optionsComboMap,
}) => {
  // 1. Cycle through selectedOptions, grab `value`, and get the appropriate
  // available options maps from optionsComboMap
  // 2. Consolidate these into a single object
  const availableOptionsMap = Object.values(selectedOptions).reduce(
    (optionsMap, value) => ({
      ...optionsMap,
      ...optionsComboMap[value],
    }),
    {}
  );

  // 3. Run through `options` and for each option type,
  return options.map(({ values, name, ...rest }) => ({
    name,
    values: values.map((value) => ({
      value,
      availableForSale: availableOptionsMap[name][value],
    })),
    ...rest,
  }));
};

export const flattenOptionValues = (values) =>
  values.map((values) => values.value);

export const keyGraphProductDataList = (productList) => {
  return productList.reduce((res, { tags, options, ...rest }) => {
    const key = getProductKeyFromHandle(rest.handle);
    // Flatten product tags data
    const productTags = tags.map((tag) => tag.value);
    // Flatten product options
    const productOptions = options.map(({ name, values }) => ({
      name,
      values: flattenOptionValues(values),
      useSwatchPicker: name === SHOPIFY_SWATCH_OPTION_NAME,
    }));

    // Key data by product_key
    res[key] = {
      ...rest,
      tags: productTags,
      options: productOptions,
    };

    return res;
  }, {});
};

export const constructImageGalleryItems = ({
  productShopifyData,
  thumbnailImageKey = GALLERY_THUMBNAIL_IMAGE_KEY,
  featuredImageKey = GALLERY_PRODUCT_IMAGE_KEY,
}) => {
  const hasVariants = productHasOtherVariants(productShopifyData);

  // If this product has variants, then each variant data from Shopify
  // will their own thumbnail/product image.
  // This assumes that if the product has variants, the image gallery images
  // will be stored in the metafields section of the Shopify Product
  if (hasVariants) {
    const galleryItems = productShopifyData.variants.map((variant) => {
      return {
        thumbnail: {
          src: getMetafield(variant, thumbnailImageKey),
          altText: getMetafield(variant, IMAGE_ALT_TEXT_KEY),
        },
        product: {
          src: getMetafield(variant, featuredImageKey),
          altText: getMetafield(variant, IMAGE_ALT_TEXT_KEY),
        },
      };
    });

    return constructImageGalleryImages(galleryItems);
  }

  const images = getShopifyGalleryImages(
    productShopifyData.images,
    productShopifyData.title
  );

  // Otherwise, all images uploaded directly to the Shopify product will
  // be used for the image gallery
  return images.map(({ src, altText }) => ({
    src,
    alt: altText || productShopifyData.title,
  }));
};

export function parseDelimitedShopifyMetafieldData({
  delimiter = METAFIELD_LIST_DELIMITER,
  metafieldData,
}) {
  return metafieldData.split(delimiter);
}

export const constructImageColorGalleryItems = ({
  productShopifyData,
  color,
}) => {
  // This is for products with color variants and require a swatch picker.
  // The image gallery images will be stored in the metafields section of the product variants.
  const variants = productShopifyData.variants;
  const variantColorMatch = getVariantUsingSwatchPicker(variants, color);
  const galleryImageString = getMetafield(
    variantColorMatch,
    COLOR_SPECIFIC_GALLERY_IMAGES_KEY
  );
  // Assumes the alt text is in the same order as the gallery images
  // Defaults to empty array in case alt text not present on a product.
  // This should not be the case however and is just a graceful way to handle
  // errors for now.
  const galleryImageAltTextString =
    getMetafield(
      variantColorMatch,
      COLOR_SPECIFIC_GALLERY_IMAGES_ALT_TEXT_KEY
    ) || "";
  const galleryImages = parseDelimitedShopifyMetafieldData({
    metafieldData: galleryImageString,
  });
  const galleryImagesAltText = parseDelimitedShopifyMetafieldData({
    metafieldData: galleryImageAltTextString,
  });

  return galleryImages.map((image, index) => ({
    src: image,
    alt:
      galleryImagesAltText[index] || `${productShopifyData.title} - ${color}`,
  }));
};

export const getImageAltText = ({ altText, productName, variantName }) => {
  // If altText is present, return that
  if (altText) {
    return altText;
  }

  // If not, first check to see if this is a variant.
  // If so, construct the alt text to be the <product name> - <variant name>
  if (variantName) {
    return `${productName} - ${variantName}`;
  }

  // Else just return the product name
  return productName;
};

// This assumes that metafields is a GraphQL object
export const isProductNew = (metafields) => {
  const flattenedMetafields = constructMetafieldsObject(metafields);
  return flattenedMetafields[IS_NEW_KEY].toLowerCase() === "true";
};

export const getRecommendedProducts = ({
  productKeys,
  allProducts, // @tedlin182 - See if can move this into a Util component
  limit = 0, // '0' means return all of the rec products
}) => {
  const mainProductKeys = {};

  // Loop through list of productKeys you want to fetch
  // recommended products for. We do care about order,
  // so use an array vs. object
  const recProductKeyList = productKeys.reduce((list, productKey) => {
    const product = allProducts[productKey];

    // Track keys of products we are trying to find rec products for.
    mainProductKeys[productKey] = true;

    // If product data exists, then continue. Else, skip this product
    // and don't include in rec products list
    if (product) {
      // Get the recommended product metafield data from each
      // product. Product data is accesses by the Shop Redux state > allProducts
      const recsProductList = getMetafield(product, RECOMMENDED_PRODUCTS_KEY);

      // If recommended products metafield is set for a product,
      // then add those to the overall rec products list
      if (recsProductList) {
        return [...list, ...recsProductList.split(METAFIELD_LIST_DELIMITER)];
      }
    }

    // Else just return the original list
    return list;
  }, []);

  // Filter out the product keys of the original products passed in
  const newRecProductKeys = recProductKeyList.filter(
    (key) => !mainProductKeys[key]
  );

  // Dedupe recommended products keys
  const dedupedRecProductKeys = [...new Set(newRecProductKeys)];

  // Return recommended product data
  // If limit is 0, null, undefined, return all recommended products
  if (!limit) {
    return dedupedRecProductKeys.map((key) => allProducts[key]);
  }

  // Else, return the specified number of recommended products
  // dictated by limit
  return dedupedRecProductKeys
    .slice(0, limit)
    .filter((key) => allProducts[key]) // Filter out invalid product keys
    .map((key) => allProducts[key]); // Just return the product data for the key
};

// Take the cart items product data list
// and return the correct recommended products list
export const getCartRecommendedProducts = ({
  // List of products to get recommended products for
  lineItems,
  // Map of all products to access product data
  allProducts,
}) => {
  // - Get product keys from line items customAttributes
  const cartProductsByKey = lineItems.map((item) =>
    getLineItemCustomAttribute(item, PRODUCT_KEY)
  );

  return getRecommendedProducts({
    productKeys: cartProductsByKey,
    allProducts,
    limit: 1,
  });
};

// Find the first variant match for a given swatch value
// Ex. For "violet" color, look through all variants' individual options like
// violet, size S and violet, size M and just return the first violet variant
// found.
export const getVariantUsingSwatchPicker = (variants, swatchValue) => {
  const variantMatch = variants.find((variant) => {
    const options = constructVariantOptionsObject(variant.selectedOptions);

    return options[SHOPIFY_SWATCH_OPTION_NAME] === swatchValue;
  });

  return variantMatch;
};

// Find variant with options matching all user-selected options
// @tedlin182 - Add test
export const getVariantWithSelectedOptions = (
  variants,
  userSelectedOptions
) => {
  const variantMatch = variants.find((variant) => {
    const { type, ...rest } = constructVariantOptionsObject(
      variant.selectedOptions
    );

    return isEqual(rest, userSelectedOptions);
  });

  // Return nulled default value if no variant match is found to
  // indicate variant isn't available
  return variantMatch || { id: null, price: null };
};

export const constructProductSliderData = (shopifyProductList) => {
  return shopifyProductList.map((productShopifyData) => {
    const productData = constructCtaProductData(productShopifyData);
    const { price } = getVariantData(productShopifyData);

    return {
      ...productData,
      price,
      handle: productShopifyData.handle,
      url: getProductDetailPagePath(productShopifyData.handle),
      variants: productShopifyData.variants,
      images: productShopifyData.images,
      editorial: getMetafield(productShopifyData, EDITORIAL_BLURB_COPY_KEY),
    };
  });
};

// Take single product option with 'name' option name key and
// 'values' array of option values
// eg. { [name]: ['value1', 'value2', 'value3']}
// This will be used primarily with SwatchPicker so that we can
// pass all option data including thumbnail image in a single blob
export const constructSwatchOptionsData = ({
  variants,
  option: { values },
  swatchImageKey = SWATCH_IMAGE_KEY,
}) => {
  // We need the following for SwatchPicker
  return values.map(({ value, availableForSale }) => {
    const variantMatch = getVariantUsingSwatchPicker(variants, value);

    return {
      value,
      image: {
        src: getMetafield(variantMatch, swatchImageKey),
        alt: value,
      },
    };
  });
};

export const findSelectedSwatchOption = (selectedOptions) => {
  for (const name in selectedOptions) {
    if (name === SHOPIFY_SWATCH_OPTION_NAME) {
      return selectedOptions[name];
    }
  }

  return null;
};

export const hasMultipleOptions = (options) => options.length > 1;

export const getAvailableOptionsList = ({
  selectedOptions,
  options,
  optionsComboMap,
  variants,
}) => {
  if (hasMultipleOptions(options)) {
    return getAllAvailableOptions({
      selectedOptions,
      options,
      optionsComboMap,
    });
  }

  return constructAvailableOptionsListFromVariants(options, variants);
};

export const getCurrentSelectedOptionValue = (values, selectedValue) => {
  const value = values.find(({ value }) => value === selectedValue);

  return { value, index: values.indexOf(value) };
};

export const getDefaultProductOptionsSelectorStateValues = (variant) => {
  if (variant) {
    return {
      defaultSelectedOptions: constructVariantOptionsObject(
        variant.selectedOptions
      ),
      defaultSelectedOptionValue: variant.selectedOptions[0].value,
    };
  }

  return {
    defaultSelectedOptions: {},
    defaultSelectedOptionValue: null,
  };
};

export const getProductOptionsSelectorProviderValues = ({
  variant,
  options,
  variants,
  selectedOptions,
  optionsComboMap,
}) => {
  if (variant) {
    // constructAvailableOptionCombinationsMap only needs to execute once for each product
    // and since it's an expensive operation to create the available option combination map,
    // we memoize the results so that we don't need to re-run constructAvailableOptionCombinationsMap and
    // simply fetch the cached results
    return {
      // Here, we need to scan ALL current selectedOptions and get the available options list
      // in order to update all option group UI. If we have multiple options, we need to compare
      // against the combination map.
      // If a no or single option type is present, then just do a simple construction
      // of the options list without a combination map
      availableOptionsList: getAvailableOptionsList({
        selectedOptions,
        options,
        optionsComboMap,
        variants,
      }),
      selectedVariant: getVariantWithSelectedOptions(variants, selectedOptions),
    };
  }

  return {
    availableOptionsList: [],
    selectedVariant: null,
  };
};

export const findVariantBySlug = (variants, variantSlug) => {
  let variantIndex = -1;

  const variant = variants.find((variantData) => {
    variantIndex++;

    return getMetafield(variantData, VARIANT_SLUG) === variantSlug;
  });

  return {
    variant: variant || {},
    variantIndex,
  };
};

export const isDiscountCodeApplied = (checkout, discountCode) =>
  checkout.discountApplications.some(({ code }) => code === discountCode);

export const discountsArePresent = (checkout) =>
  !isEmpty(checkout.discountApplications);

export const getLineItemDiscount = (lineItem) => lineItem.discounts;

export const getShopifyProductId = ({ id, productType = "Product" }) =>
  window.atob(id).split(`gid://shopify/${productType}/`)[1];

const shop = {
  constructImageData,
  getProductKeyFromHandle,
  getMatchingSleepGoalCollection,
  getSleepGoalCollectionKeyFromHandle,
  getSleepGoalCollection,
  getProductSleepGoalCollectionKey,
  getTotalCartQuantity,
  getProductName,
  constructImageGalleryImages,
  isInCart,
  addLineItemToOrderedLineItems,
  removeLineItemFromOrderedLineItems,
  setInitialOrderedLineItems,
  constructMetafieldsObject,
  constructVariantOptionsObject,
  getProductData,
  getVariantData,
  getMetafield,
  getShopifyVariantData,
  getLineItemDataByVariantId,
  getLineItemCustomAttribute,
  productHasOtherVariants,
  createCustomAttributesFromMetafields,
  constructCtaProductData,
  sanitizeMarkup,
  getShopifyProductCartImage,
  getShopifyProductSliderImage,
  getShopifyProductFeaturedImage,
  getShopifyProductUpsellImage,
  getShopifyProductImageAltText,
  getShopifyProductCartDescription,
  getShopifyGalleryImages,
  queryMultipleCollections,
  queryCollectionByHandle,
  keyGraphCollectionDataList,
  keyGraphProductDataList,
  constructImageGalleryItems,
  getImageAltText,
  isProductNew,
  getRecommendedProducts,
  getCartRecommendedProducts,
  constructProductSliderData,
  getCollectionLandingPagePath,
  getProductDetailPagePath,
  // TODO: Move into separate productOptions.js utils file
  constructAvailableOptionsMap,
  constructAvailableOptionsComboBaseMap,
  constructAvailableOptionCombinationsMap,
  constructAvailableOptionsListFromVariants,
  getVariantUsingSwatchPicker,
  getVariantWithSelectedOptions,
  getVariantDataByMetafield,
  getAllAvailableOptions,
  flattenOptionValues,
  findSelectedSwatchOption,
  hasMultipleOptions,
  getAvailableOptionsList,
  getCurrentSelectedOptionValue,
  getDefaultProductOptionsSelectorStateValues,
  getProductOptionsSelectorProviderValues,
  findVariantBySlug,
  isDiscountCodeApplied,
  discountsArePresent,
  getLineItemDiscount,
  getShopifyProductId,
};

export default shop;
